import jsPDF from 'jspdf';
import { ProfessionalPaymentRecord } from '../types/professional-payment';
import clinicInfo, { clinicContactLine } from '../config/clinicInfo';
import { Timestamp } from 'firebase/firestore';

export class ProfessionalPaymentPDFService {
  private static async loadLogoAsBase64(): Promise<string | null> {
    try {
      const response = await fetch(clinicInfo.reportBranding.logoMark);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Não foi possível carregar o logo:', error);
      return null;
    }
  }

  private static formatDate(date: Date | Timestamp): string {
    const d = date instanceof Timestamp ? date.toDate() : date;
    return d.toLocaleDateString('pt-BR');
  }

  static async generatePDF(payment: ProfessionalPaymentRecord): Promise<jsPDF> {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    const logoBase64 = await this.loadLogoAsBase64();

    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', 15, yPosition, 30, 30);
      } catch (error) {
        console.warn('Erro ao adicionar logo ao PDF:', error);
      }
    }

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(clinicInfo.reportBranding.displayName.toUpperCase(), pageWidth / 2, yPosition + 8, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(clinicInfo.razaoSocial, pageWidth / 2, yPosition + 14, { align: 'center' });

    doc.setFontSize(8);
    doc.text(`CNPJ: ${clinicInfo.cnpj}`, pageWidth / 2, yPosition + 19, { align: 'center' });
    doc.text(clinicInfo.endereco.enderecoCompleto, pageWidth / 2, yPosition + 23, { align: 'center' });
    doc.text(clinicContactLine, pageWidth / 2, yPosition + 27, { align: 'center' });

    yPosition = 55;

    doc.setDrawColor(0, 102, 204);
    doc.setLineWidth(0.5);
    doc.line(15, yPosition, pageWidth - 15, yPosition);

    yPosition += 10;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 102, 204);
    doc.text('RELATÓRIO DE REPASSE PROFISSIONAL', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 12;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    doc.setFont('helvetica', 'bold');
    doc.text('Dados do Profissional:', 15, yPosition);
    yPosition += 7;

    doc.setFont('helvetica', 'normal');
    doc.text(`Nome: ${payment.professionalName}`, 15, yPosition);
    yPosition += 6;

    doc.text(`Período: ${payment.reportPeriod}`, 15, yPosition);
    yPosition += 6;

    doc.text(`Tipo de Período: ${payment.reportType === 'billing_period' ? 'Faturamento' : 'Data Fixa'}`, 15, yPosition);
    yPosition += 6;

    const startDate = this.formatDate(payment.startDate);
    const endDate = this.formatDate(payment.endDate);
    doc.text(`Data do Período: ${startDate} a ${endDate}`, 15, yPosition);
    yPosition += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Resumo:', 15, yPosition);
    yPosition += 7;

    doc.setFont('helvetica', 'normal');
    doc.text(`Total de Sessões: ${payment.totalSessions}`, 15, yPosition);
    yPosition += 6;

    doc.text(`Sessões com Evolução: ${payment.sessionsWithEvolution}`, 15, yPosition);
    yPosition += 6;

    doc.text(`Sessões sem Evolução: ${payment.totalSessions - payment.sessionsWithEvolution}`, 15, yPosition);
    yPosition += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 128, 0);
    doc.text(`Valor Total: R$ ${payment.totalAmount.toFixed(2)}`, 15, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 12;

    if (payment.isPaid && payment.paidAt) {
      doc.setFillColor(220, 252, 231);
      doc.rect(15, yPosition - 5, pageWidth - 30, 20, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(22, 101, 52);
      doc.text('Status: PAGO', 20, yPosition);
      yPosition += 7;

      doc.setFont('helvetica', 'normal');
      doc.text(`Data do Pagamento: ${this.formatDate(payment.paidAt)}`, 20, yPosition);
      yPosition += 6;

      if (payment.paymentMethod) {
        doc.text(`Método de Pagamento: ${payment.paymentMethod}`, 20, yPosition);
        yPosition += 6;
      }

      if (payment.notes) {
        doc.text(`Observações: ${payment.notes}`, 20, yPosition);
      }

      doc.setTextColor(0, 0, 0);
      yPosition += 12;
    } else {
      doc.setFillColor(254, 249, 195);
      doc.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(161, 98, 7);
      doc.text('Status: PENDENTE', 20, yPosition);
      doc.setTextColor(0, 0, 0);
      yPosition += 12;
    }

    doc.setFont('helvetica', 'bold');
    doc.text('Detalhamento dos Atendimentos:', 15, yPosition);
    yPosition += 8;

    const tableHeaders = ['Data', 'Horário', 'Paciente', 'Local', 'Evolução', 'Valor'];
    const colWidths = [22, 18, 50, 45, 20, 25];
    const startX = 15;
    let currentX = startX;

    doc.setFillColor(240, 240, 240);
    doc.rect(startX, yPosition - 5, pageWidth - 30, 8, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');

    tableHeaders.forEach((header, index) => {
      doc.text(header, currentX + 2, yPosition);
      currentX += colWidths[index];
    });

    yPosition += 5;
    doc.setFont('helvetica', 'normal');

    payment.appointmentDetails.forEach((detail, index) => {
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;

        currentX = startX;
        doc.setFillColor(240, 240, 240);
        doc.rect(startX, yPosition - 5, pageWidth - 30, 8, 'F');
        doc.setFont('helvetica', 'bold');

        tableHeaders.forEach((header, i) => {
          doc.text(header, currentX + 2, yPosition);
          currentX += colWidths[i];
        });

        yPosition += 5;
        doc.setFont('helvetica', 'normal');
      }

      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(startX, yPosition - 4, pageWidth - 30, 6, 'F');
      }

      currentX = startX;

      doc.text(detail.date || '-', currentX + 2, yPosition);
      currentX += colWidths[0];

      doc.text(detail.time || '-', currentX + 2, yPosition);
      currentX += colWidths[1];

      const patientName = detail.patientName.length > 25
        ? detail.patientName.substring(0, 22) + '...'
        : detail.patientName;
      doc.text(patientName, currentX + 2, yPosition);
      currentX += colWidths[2];

      const location = (detail.location || '-').length > 20
        ? detail.location.substring(0, 17) + '...'
        : (detail.location || '-');
      doc.text(location, currentX + 2, yPosition);
      currentX += colWidths[3];

      doc.text(detail.hasEvolution ? 'Sim' : 'Não', currentX + 2, yPosition);
      currentX += colWidths[4];

      doc.text(`R$ ${detail.value.toFixed(2)}`, currentX + 2, yPosition);

      yPosition += 6;
    });

    yPosition += 5;

    doc.setDrawColor(0, 102, 204);
    doc.setLineWidth(0.5);
    doc.line(15, yPosition, pageWidth - 15, yPosition);

    yPosition += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL GERAL:', 15, yPosition);
    doc.text(`${payment.totalSessions} sessões`, pageWidth / 2 - 20, yPosition);
    doc.setTextColor(0, 128, 0);
    doc.text(`R$ ${payment.totalAmount.toFixed(2)}`, pageWidth - 45, yPosition);
    doc.setTextColor(0, 0, 0);

    yPosition = pageHeight - 20;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);

    const generatedAt = new Date().toLocaleString('pt-BR');
    doc.text(`Documento gerado em: ${generatedAt}`, pageWidth / 2, yPosition, { align: 'center' });
    doc.text('Este documento é um relatório interno de repasse profissional', pageWidth / 2, yPosition + 4, { align: 'center' });

    return doc;
  }

  static async downloadPDF(payment: ProfessionalPaymentRecord): Promise<void> {
    const pdf = await this.generatePDF(payment);
    const fileName = `repasse_${payment.professionalName.replace(/\s+/g, '_')}_${payment.reportPeriod.replace(/\//g, '-')}.pdf`;
    pdf.save(fileName);
  }
}
